import React, { useState } from 'react';

const BrandManagement = ({ brands, items, onAddBrand, onDeleteBrand }) => {
  const [newBrand, setNewBrand] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [deletingBrand, setDeletingBrand] = useState(null);

  const handleAdd = async (e) => {
    e.preventDefault();
    const trimmed = newBrand.trim();
    if (!trimmed) {
      setError('Brand name cannot be empty');
      return;
    }
    if (brands.includes(trimmed)) {
      setError('Brand already exists');
      return;
    }
    setError('');
    setSaving(true);
    try {
      await onAddBrand(trimmed);
      setNewBrand('');
    } catch (err) {
      setError('Failed to add brand');
    } finally {
      setSaving(false);
    }
  };

  const getItemCount = (brand) => {
    return items.filter(item => item.brand === brand).length;
  };

  return (
    <div className="max-w-3xl mx-auto">
      <h1 className="text-3xl font-bold text-gray-900 mb-6">Brand Management</h1>

      {/* Add Brand Form */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-xl font-bold mb-4">Add Brand</h2>
        <form onSubmit={handleAdd} className="flex gap-3">
          <input
            type="text"
            value={newBrand}
            onChange={(e) => { setNewBrand(e.target.value); setError(''); }}
            className={`flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
              error ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="Enter brand name"
          />
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? 'Adding...' : 'Add Brand'}
          </button>
        </form>
        {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
      </div>

      {/* Brands Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {brands.length === 0 ? (
          <div className="p-12 text-center">
            <p className="text-gray-500">No brands yet. Add your first brand above.</p>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Brand Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Items
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {brands.map(brand => {
                const count = getItemCount(brand);
                return (
                  <tr key={brand} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{brand}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">{count} item{count !== 1 ? 's' : ''}</td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={async () => {
                          if (count > 0) {
                            if (!window.confirm(`"${brand}" is used by ${count} item(s). Delete anyway?`)) return;
                          }
                          setDeletingBrand(brand);
                          try {
                            await onDeleteBrand(brand);
                          } finally {
                            setDeletingBrand(null);
                          }
                        }}
                        disabled={deletingBrand === brand}
                        className="text-red-600 hover:text-red-900 text-sm disabled:opacity-50"
                      >
                        {deletingBrand === brand ? 'Deleting...' : 'Delete'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default BrandManagement;
